import React from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';

import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';

import { BUDGET } from 'business/constants';
import { useClientHasModule } from 'hooks/client';
import { useCreateUser } from './hooks/useCreateUser';
import { useValidateUserForm } from './hooks/useValidateUserForm';
import { useGetEffectiveClientScopes } from './hooks/scopes';

import UserFormActivationSection from './components/UserFormActivationSection';
import UserFormGeneralSection from './components/UserFormGeneralSection/UserFormGeneralSection';
import {
    useUserCreationFormContext,
    useUserCreationFormDispatchContext,
} from './CreateUserContext/CreateUserFormContext';
import { useGetScopeFormSteps } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/hooks/useGetScopeFormSteps';

const CreateUserModal = ({
    setIsModalOpen,
    existingUsersEmails,
}: {
    setIsModalOpen: (arg: boolean) => void;
    existingUsersEmails: string[];
}): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [shouldSendActivationEmail, setShouldSendActivationEmail] =
        React.useState(true);
    const userCreationForm = useUserCreationFormContext();
    const { hasDispatchBeenCalled } = useUserCreationFormDispatchContext();
    const warningMessage = useValidateUserForm(
        userCreationForm,
        existingUsersEmails,
    );
    const hasBudgetModule = useClientHasModule(BUDGET);
    const { scopes: clientScopes, isLoading: isClientScopesLoading } =
        useGetEffectiveClientScopes();

    const scopesFormSteps = useGetScopeFormSteps(clientScopes);

    const createUserMutation = useCreateUser(
        userCreationForm,
        hasBudgetModule,
        shouldSendActivationEmail,
    );

    const onClose = () => {
        if (!hasDispatchBeenCalled.current) {
            setIsModalOpen(false);
            return;
        }
        if (
            window.confirm(t('adminSpace.createUser.leavingModalConfirmation'))
        ) {
            setIsModalOpen(false);
        }
    };

    const onCreateUserAction = (): void => {
        createUserMutation.mutate();
        setIsModalOpen(false);
    };
    const steps: ModalStepT[] = [
        {
            confirmAction: {
                enabled: !isClientScopesLoading && !warningMessage,
                tooltipMessage: warningMessage,
            },
            node: <UserFormGeneralSection warningMessage={warningMessage} />,
            title: (
                <>
                    <Typography variant="h4">
                        {t('adminSpace.createUser.dialogTitle', {
                            section: t(
                                'adminSpace.createUser.modalStepper.generalSection.title',
                            ),
                        })}
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        {t(
                            'adminSpace.createUser.modalStepper.generalSection.subtitle',
                        )}
                    </Typography>
                </>
            ),
        },
        ...scopesFormSteps,
        {
            confirmAction: {
                label: 'common.complete',
                onClick: () => {
                    onCreateUserAction();
                },
                shouldTriggerDefaultOnClose: false,
                className: 'pendo-create-new-user-confirm',
            },
            node: (
                <UserFormActivationSection
                    setShouldSendActivationEmail={setShouldSendActivationEmail}
                    shouldSendActivationEmail={shouldSendActivationEmail}
                />
            ),
            title: (
                <Typography variant="h4">
                    {t('adminSpace.createUser.dialogTitle', {
                        section: t(
                            'adminSpace.createUser.modalStepper.activationSection.subtitle',
                        ),
                    })}
                </Typography>
            ),
        },
    ];

    return (
        <MultiStepModal
            isSmallHeight
            maxWidth="md"
            onClose={onClose}
            steps={steps}
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
        />
    );
};

export default CreateUserModal;
